import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import TrashIcon from '@assets/icons/trash-small.svg';
import { AddNewFooter } from '@/components/add-new-footer';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import {
  DeletePaymentActionSheet,
  PaymentMethodListOption,
} from './delete-payment-action-sheet';
import { DeletePaymentRightAction } from './delete-payment-right-action';
import {
  Props as ListItemProps,
  ListItemSeparator,
  SwipeableWithInstantClose,
} from '@/components/list-item';
import { PayByCheckFooter } from './pay-by-check-footer';
import { PayByKlarnaFooter } from './pay-by-klarna-footer';
import {
  PaymentMethodOption,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PaymentMethodType } from '@/enums';
import {
  PixelRatio,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { RecordCashPaymentFooter } from './record-cash-payment-footer';
import { RecordCheckPaymentFooter } from './record-check-payment-footer';
import { RecyclerList, RecyclerListRef } from '@/components/recycler-list';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import type { KlarnaVariant } from '@/hooks/payments/klarna-types';

interface Props {
  contentContainerStyle?: StyleProp<ViewStyle>;
  footerBanner?: React.ReactNode;
  isDistributorUser?: boolean;
  klarnaPaymentMethods?: KlarnaVariant[];
  onAddMethod: () => void;
  onAddPAD?: () => void;
  onConnectBankAccount?: () => void;
  onPayByCheck?: () => void;
  onRecordCashPayment?: () => void;
  onRecordCheckPayment?: () => void;
  onSelectKlarna?: (variant: KlarnaVariant) => void;
  onSelectMethod?: (paymentMethod?: PaymentMethodOption) => void;
}

const fontScale = PixelRatio.getFontScale();
const ICON_SIZE = 18 * fontScale;

export const PaymentMethodSelector: React.FC<Props> = ({
  contentContainerStyle,
  footerBanner,
  isDistributorUser = false,
  klarnaPaymentMethods,
  onAddMethod,
  onAddPAD,
  onConnectBankAccount,
  onPayByCheck,
  onRecordCashPayment,
  onRecordCheckPayment,
  onSelectKlarna,
  onSelectMethod,
}) => {
  const [styles, theme] = useStyles();
  const { showErrorToast } = useToasts();
  const { bottom } = useSafeAreaInsets();
  const { enabledPaymentMethods, paymentMethods, removePaymentMethod } =
    usePaymentsContext();
  const padEnabled = enabledPaymentMethods.includes(PaymentMethodType.PAD);
  const checkCaptureEnabled = useDynamicConfigFlag(
    'jack_henry_checks_config',
    'check_capture_enabled',
  );
  const showPayByCheck =
    Boolean(onPayByCheck) && checkCaptureEnabled && Platform.OS !== 'web';
  const showHelperText = showPayByCheck;
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodListOption | null>(null);

  const listData = useMemo(() => {
    if (isDistributorUser) {
      return [];
    }
    return (paymentMethods || []).map<PaymentMethodListOption>((pm) => ({
      metadata: pm,
      title: pm.displayText,
      value: pm.uuid,
    }));
  }, [isDistributorUser, paymentMethods]);

  const listRef = useRef<RecyclerListRef<PaymentMethodListOption>>(null);
  const swipeableItemRef = useRef<SwipeableWithInstantClose>(null);
  const closeSwipeable = useCallback(() => {
    swipeableItemRef.current?.close();
    swipeableItemRef.current = null;
  }, []);

  const listItemProps: Partial<ListItemProps<PaymentMethodListOption>> =
    useMemo(
      () => ({
        listItemStyle: styles.listItem,
        renderMainContent: (option) => {
          const cashbackPercentage = option.metadata?.cashbackPercentage;
          const showBadge = Boolean(
            cashbackPercentage && cashbackPercentage > 0,
          );

          return (
            <View
              style={[
                styles.mainContent,
                showBadge && styles.mainContentWithBadge,
              ]}
            >
              <Typography numberOfLines={1} variant="heading-sm">
                {option.title}
              </Typography>
              {showBadge ? (
                <Badge
                  alignment="center"
                  label={i18n.t('tab_card.cashback_badge', {
                    cashbackPercentage,
                  })}
                  size={BadgeSize.SMALL}
                  variant={BadgeVariant.SUCCESS}
                />
              ) : null}
            </View>
          );
        },
        renderRightComponent: (option) => {
          return Platform.OS === 'web' ? (
            <View style={styles.actionContainer}>
              <TouchableOpacity
                onPress={() => setSelectedPaymentMethod(option)}
                style={styles.action}
                testID="delete-payment-icon"
              >
                <TrashIcon
                  height={ICON_SIZE}
                  stroke={theme.colors.primary}
                  testID="trash-icon"
                  width={ICON_SIZE}
                />
              </TouchableOpacity>
            </View>
          ) : null;
        },
        rightComponentStyle: styles.rightComponentStyle,
        swipeableProps:
          Platform.OS === 'web'
            ? undefined
            : {
                onSwipeableRightWillOpen: (_, swipeableRef) => {
                  if (
                    swipeableItemRef.current &&
                    swipeableItemRef.current !== swipeableRef
                  ) {
                    closeSwipeable();
                  }
                  swipeableItemRef.current = swipeableRef;
                },
                renderRightActions: (option) => {
                  return (
                    <View style={styles.rightActionContainer}>
                      <DeletePaymentRightAction
                        backgroundColor={theme.colors.alertWarning}
                        Icon={TrashIcon}
                        iconProps={{
                          stroke: theme.colors.primary,
                        }}
                        onPress={() => setSelectedPaymentMethod(option)}
                        text={i18n.t('delete_payment')}
                      />
                    </View>
                  );
                },
              },
        testID: 'payment-method-list-item',
      }),
      [closeSwipeable, styles, theme],
    );

  return (
    <>
      <RecyclerList
        contentContainerStyle={[
          styles.recyclerContent,
          { paddingBottom: bottom },
          contentContainerStyle,
        ]}
        estimatedItemSize={56}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="handled"
        ListFooterComponent={
          <>
            {isDistributorUser ? null : (
              <>
                <ListItemSeparator style={styles.listItemSeparator} />
                <AddNewFooter
                  onSelect={onAddMethod}
                  size="small"
                  title={i18n.t('add_payment_method')}
                />
                {onConnectBankAccount ? (
                  <AddNewFooter
                    onSelect={onConnectBankAccount}
                    size="small"
                    title={i18n.t('connect_bank_account')}
                  />
                ) : null}
                {padEnabled && onAddPAD ? (
                  <AddNewFooter
                    onSelect={onAddPAD}
                    size="small"
                    title={i18n.t('add_pad_ach')}
                  />
                ) : null}
              </>
            )}
            {onSelectKlarna && klarnaPaymentMethods?.length
              ? klarnaPaymentMethods.map((variant) => (
                  <PayByKlarnaFooter
                    key={variant}
                    onSelect={() => {
                      Analytics.track('payments.pay_with_klarna', { variant });
                      onSelectKlarna(variant);
                    }}
                    variant={variant}
                  />
                ))
              : null}
            {onRecordCashPayment ? (
              <RecordCashPaymentFooter
                onSelect={() => {
                  Analytics.track('cash_payment.record_cash_payment.press');
                  onRecordCashPayment();
                }}
              />
            ) : null}
            {onRecordCheckPayment ? (
              <RecordCheckPaymentFooter
                onSelect={() => {
                  Analytics.track('check_payment.record_check_payment.press');
                  onRecordCheckPayment();
                }}
              />
            ) : null}
            {showPayByCheck && onPayByCheck ? (
              <PayByCheckFooter
                onSelect={() => {
                  Analytics.track('check_capture.pay_by_check.press');
                  onPayByCheck();
                }}
              />
            ) : null}
            {showHelperText ? (
              <View style={styles.helper}>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  variant="paragraph-md"
                >
                  {i18n.t('pay_by_check_helper')}
                </Typography>
              </View>
            ) : null}
            {footerBanner ? (
              <View style={styles.footerBannerContainer}>{footerBanner}</View>
            ) : null}
          </>
        }
        listItemProps={listItemProps}
        listItemSeparatorProps={{
          style: styles.listItemSeparator,
        }}
        listRef={listRef}
        onSelect={
          onSelectMethod
            ? ({ metadata }) => {
                Analytics.track('payments.select_payment_method', {
                  paymentMethodUUID: metadata?.uuid,
                });

                onSelectMethod(metadata);
              }
            : undefined
        }
        sections={[
          {
            data: listData,
            key: null,
          },
        ]}
        testID="payment-method-list"
      />
      <DeletePaymentActionSheet
        onCancel={() => setSelectedPaymentMethod(null)}
        onDelete={async () => {
          try {
            await removePaymentMethod(selectedPaymentMethod?.metadata?.uuid!);
          } catch (e) {
            showErrorToast();
          } finally {
            setSelectedPaymentMethod(null);
          }
        }}
        selectedPaymentMethod={selectedPaymentMethod}
      />
    </>
  );
};

const RIGHT_ACTION_BUTTON_WIDTH = 60;

const useStyles = createStyles((theme) => ({
  action: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: theme.sk.spacing.spaceSmall,
  },
  actionContainer: {
    flexDirection: 'row',
  },
  footerBannerContainer: {
    marginHorizontal: theme.sk.spacing.spaceMedium,
    marginTop: theme.sk.spacing.spaceMedium,
  },
  helper: {
    marginTop: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  listItem: {
    marginHorizontal: theme.sk.spacing.spaceMedium,
    paddingLeft: theme.sk.spacing.spaceSmall,
    paddingRight: theme.sk.spacing.spaceMedium,
  },
  listItemSeparator: {
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
  mainContent: {
    flex: 1,
  },
  mainContentWithBadge: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: theme.sk.spacing.spaceSmall,
  },
  recyclerContent: {
    paddingTop: theme.sk.spacing.spaceSmall,
  },
  rightActionContainer: {
    flexDirection: 'row',
    width: RIGHT_ACTION_BUTTON_WIDTH * 2,
  },
  rightComponentStyle: {
    marginLeft: theme.sk.spacing.spaceSmall,
  },
}));
